import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Search, Download, AlertCircle, Loader, RefreshCw } from 'lucide-react';
import * as dpService from 'features/dp/services/dpService';

function normalizarData(valor) {
  if (!valor || valor === '0000-00-00') return '';
  return valor.slice(0, 10);
}

export default function ImportarIXCModal({ companyName = 'MOV Fibra', unidadeId = null, onSelecionar, onCancelar }) {
  const [funcionarios, setFuncionarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');

  const carregar = useCallback(() => {
    setCarregando(true);
    setErro(null);
    dpService.listarFuncionariosIXC(companyName, unidadeId)
      .then((data) => {
        // MOV Fit retorna "colaboradores", IXC retorna "funcionarios"
        const lista = data.funcionarios ?? data.colaboradores ?? [];
        setFuncionarios(Array.isArray(lista) ? lista : []);
      })
      .catch(() => setErro('Não foi possível consultar os funcionários da empresa. Tente novamente.'))
      .finally(() => setCarregando(false));
  }, [companyName, unidadeId]);

  useEffect(() => { carregar(); }, [carregar]);

  const filtrados = useMemo(() => {
    const q = busca.toLowerCase();
    if (!q) return funcionarios;
    return funcionarios.filter(
      (f) =>
        f.nome?.toLowerCase().includes(q) ||
        f.login?.toLowerCase().includes(q) ||
        f.cpf?.includes(q) ||
        f.cargo?.toLowerCase().includes(q) ||
        f.funcao?.toLowerCase().includes(q) ||
        f.departamento?.toLowerCase().includes(q) ||
        f.setor?.toLowerCase().includes(q)
    );
  }, [funcionarios, busca]);

  const handleSelecionar = (func) => {
    const unidadeLabel = unidadeId
      ? (dpService.MOV_FIT_UNIDADES.find(u => u.id === unidadeId)?.label ?? '')
      : '';

    onSelecionar({
      _ixc: true,
      pessoal: {
        cpf:              (func.cpf ?? '').replace(/\D/g, ''),
        nome:             func.nome ?? func.login ?? '',
        nome_social:      '',
        dt_nascimento:    normalizarData(func.data_nascimento),
        sexo:             func.sexo ?? 'M',
        estado_civil:     mapEstadoCivil(func.estado_civil),
        pis_pasep:        (func.pis ?? '').replace(/\D/g, ''),
        rg_numero:        func.rg ?? '',
        rg_orgao_emissor: func.rg_orgao ?? '',
        telefone:         func.telefone ?? func.celular ?? '',
        email:            func.email ?? '',
      },
      contratual: {
        dt_admissao:              normalizarData(func.data_admissao),
        cargo_id:                 '',
        cargo_nome_ixc:           func.cargo ?? func.funcao ?? '',
        departamento_id:          '',
        departamento_nome_ixc:    func.departamento ?? func.setor ?? '',
        unidade:                  unidadeLabel,
        tipo_contrato:            'indeterminado',
        categoria_esocial:        '101',
        tipo_regime_trab:         'clt',
        tipo_regime_prev:         'rgps',
        tipo_jornada:             'semanal',
        jornada_horas_semanais:   '44',
        salario:                  func.salario ?? func.remuneracao ?? '',
        tipo_salario:             'mensal',
      },
      dependentes: [],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[color:var(--surface)] rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[color:var(--border)]">
          <div>
            <h2 className="text-lg font-bold text-[color:var(--text)]">
              {unidadeId
                ? `Importar da Pacto — ${dpService.MOV_FIT_UNIDADES.find(u => u.id === unidadeId)?.label ?? ''}`
                : 'Importar do IXC'}
            </h2>
            {!carregando && !erro && (
              <p className="text-xs text-[color:var(--text-muted)] mt-0.5">
                {filtrados.length} de {funcionarios.length} funcionários
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={carregar}
              disabled={carregando}
              title="Sincronizar com N8N"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] hover:text-teal-600 hover:border-teal-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw size={13} className={carregando ? 'animate-spin' : ''} />
              Sincronizar
            </button>
            <button onClick={onCancelar} className="text-[color:var(--text-muted)] hover:text-[color:var(--text)] transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-[color:var(--border)]">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
            <input
              type="text"
              placeholder="Buscar por nome, login, CPF, cargo ou setor..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              autoFocus
              className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {carregando ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader size={28} className="animate-spin text-teal-600" />
              <p className="text-sm text-[color:var(--text-muted)]">Buscando dados no N8N...</p>
            </div>
          ) : erro ? (
            <div className="flex flex-col items-center gap-3 m-6 p-5 rounded-xl bg-red-50 dark:bg-red-900/20">
              <div className="flex items-center gap-2 text-red-500">
                <AlertCircle size={16} />
                <span className="text-sm">{erro}</span>
              </div>
              <button
                onClick={carregar}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                <RefreshCw size={12} />
                Tentar novamente
              </button>
            </div>
          ) : filtrados.length === 0 ? (
            <div className="text-center py-12 text-[color:var(--text-muted)]">
              <p className="font-medium">Nenhum resultado encontrado</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[color:var(--surface)] z-10">
                <tr className="border-b border-[color:var(--border)]">
                  <th className="text-left py-3 px-4 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider">Nome</th>
                  <th className="text-left py-3 px-4 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider hidden sm:table-cell">CPF</th>
                  {filtrados[0]?.cargo || filtrados[0]?.funcao ? (
                    <>
                      <th className="text-left py-3 px-4 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider hidden md:table-cell">Cargo</th>
                      <th className="text-left py-3 px-4 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider hidden md:table-cell">Departamento</th>
                      <th className="text-left py-3 px-4 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider hidden lg:table-cell">Admissão</th>
                    </>
                  ) : (
                    <th className="text-left py-3 px-4 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider hidden md:table-cell">Situação</th>
                  )}
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((func) => (
                  <tr
                    key={func.id ?? func.id_externo}
                    className="border-b border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="font-medium text-[color:var(--text)]">{func.nome || func.login || '—'}</div>
                      <div className="text-xs text-[color:var(--text-muted)]">{func.email || ''}</div>
                    </td>
                    <td className="py-3 px-4 text-[color:var(--text-muted)] hidden sm:table-cell font-mono text-xs">
                      {func.cpf || '—'}
                    </td>
                    {func.cargo || func.funcao ? (
                      <>
                        <td className="py-3 px-4 text-[color:var(--text-muted)] hidden md:table-cell">
                          {func.cargo ?? func.funcao ?? '—'}
                        </td>
                        <td className="py-3 px-4 text-[color:var(--text-muted)] hidden md:table-cell">
                          {func.departamento ?? func.setor ?? '—'}
                        </td>
                        <td className="py-3 px-4 text-[color:var(--text-muted)] hidden lg:table-cell text-xs">
                          {normalizarData(func.data_admissao)
                            ? new Date(normalizarData(func.data_admissao) + 'T00:00:00').toLocaleDateString('pt-BR')
                            : '—'}
                        </td>
                      </>
                    ) : (
                      <td className="py-3 px-4 hidden md:table-cell">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          func.situacao === 'AT'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {func.situacao === 'AT' ? 'Ativo' : func.situacao ?? '—'}
                        </span>
                      </td>
                    )}
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleSelecionar(func)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors ml-auto"
                      >
                        <Download size={12} />
                        Importar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────── */
function mapEstadoCivil(codigo) {
  const map = {
    S: 'solteiro',
    C: 'casado',
    D: 'divorciado',
    V: 'viuvo',
    U: 'uniao_estavel',
  };
  return map[codigo] ?? 'solteiro';
}
